import { useState } from 'react';
import { Box, Button, makeStyles } from '@material-ui/core';
import { DataGrid, GridActionsCellItem, GridColDef, GridToolbar } from '@mui/x-data-grid';
import theme from 'App/theme';
import Panel from 'common/components/UI/Panel';
import { useListCompanies } from 'common/hooks/company/useListCompanies';
import { DeleteIcon, EditIcon } from 'common/utils/Icons';
import { CreateCompanyDialog } from './CreateCompanyDialog';
import { DeleteCompanyDialog } from './DeleteCompanyDialog';
import { EditCompanyDialog } from './EditCompanyDialog';

const styles = {
    root: {
        margin: theme.spacing(2),
        paddingTop: theme.spacing(2),
    },
    cellBorder: {
        margin: theme.spacing(2),
        '& .MuiDataGrid-columnHeader': {
            borderTop: '2px solid #f0f0f0',
        },
        '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
            borderRight: '1px solid #f0f0f0',
        },
    },
};

const useStyles = makeStyles(styles);

export const CompanyManagementPage = () => {
    const classes = useStyles();
    const { isLoading, companies } = useListCompanies();
    const [createCompanyDialogOpen, setCreateCompanyDialogOpen] = useState(false);
    const [editCompanyDialogOpen, setEditCompanyDialogOpen] = useState(false);
    const [editCompanyId, setEditCompanyId] = useState(0);
    const [editCompanyName, setEditCompanyName] = useState('');
    const [deleteCompanyDialogOpen, setDeleteCompanyDialogOpen] = useState(false);
    const [deleteCompanyId, setDeleteCompanyId] = useState(0);
    const [deleteCompanyName, setDeleteCompanyName] = useState('');

    const handleEditCompany = (id: number, name: string) => () => {
        setEditCompanyId(id);
        setEditCompanyName(name);
        setEditCompanyDialogOpen(true);
    };

    const handleDeleteCompany = (id: number, name: string) => () => {
        setDeleteCompanyId(id);
        setDeleteCompanyName(name);
        setDeleteCompanyDialogOpen(true);
    };

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: 'Name',
            width: 300,
            sortable: true,
            disableColumnMenu: true,
        },
        {
            field: 'edit',
            type: 'actions',
            headerName: 'Edit',
            width: 75,
            cellClassName: 'actions',
            getActions: ({ id, row }) => {
                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Roles"
                        className="textPrimary"
                        title="Roles Assignment"
                        onClick={handleEditCompany(row.id, row.name)}
                        color="inherit"
                    />,
                ];
            },
        },
        {
            field: 'delete',
            type: 'actions',
            headerName: 'Delete',
            width: 75,
            cellClassName: 'actions',
            getActions: ({ id, row }) => {
                return [
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Roles"
                        className="textPrimary"
                        title="Roles Assignment"
                        onClick={handleDeleteCompany(row.id, row.name)}
                        color="inherit"
                    />,
                ];
            },
        },
    ];

    return (
        <>
            <div className={classes.root}>
                <Panel>
                    <Box width="100%">
                        <DataGrid
                            loading={isLoading}
                            rows={companies || []}
                            getRowId={row => row.id}
                            columns={columns}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10,
                                    },
                                },
                            }}
                            pageSizeOptions={[10]}
                            hideFooterSelectedRowCount={true}
                            className={classes.cellBorder}
                            sx={{ bgcolor: 'background.paper' }}
                            autoHeight
                            slots={{ toolbar: GridToolbar }}
                        />
                        <Button variant="contained" color="primary" size="small" onClick={() => setCreateCompanyDialogOpen(true)}>
                            Create
                        </Button>
                    </Box>
                </Panel>
            </div>
            <CreateCompanyDialog open={createCompanyDialogOpen} close={() => setCreateCompanyDialogOpen(false)} />
            {editCompanyDialogOpen && (
                <EditCompanyDialog
                    open={editCompanyDialogOpen}
                    close={() => setEditCompanyDialogOpen(false)}
                    id={editCompanyId}
                    name={editCompanyName}
                />
            )}
            <DeleteCompanyDialog
                open={deleteCompanyDialogOpen}
                close={() => setDeleteCompanyDialogOpen(false)}
                id={deleteCompanyId}
                name={deleteCompanyName}
            />
        </>
    );
};
